import type { SelectHTMLAttributes } from 'react';
import { Champ } from './Champ';

/**
 * **Un choix dans une liste fermée.**
 *
 * **L'élément natif, encapsulé et non remplacé.** `design-system-first` tranche ce cas
 * nommément : « une skill de minimalisme active en même temps poussera vers l'élément
 * natif brut plutôt que le composant. Résolution : **encapsuler le natif, ne pas le
 * remplacer**. » Un sélecteur maison coûterait le clavier, le tactile, la liste longue et
 * le lecteur d'écran, pour un gain d'apparence.
 *
 * **Il ne porte aucune valeur vide implicite.** Quand « rien » est un choix valide — la
 * racine de l'espace, par exemple —, c'est une option comme les autres, avec son libellé.
 * Une option vide sans texte se lit comme un bogue.
 */
type Option = {
  readonly valeur: string;
  readonly libelle: string;
};

type Props = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'className' | 'children'
> & {
  readonly libelle: string;
  readonly aide?: string;
  readonly erreur?: string;
  readonly options: readonly Option[];
};

export function Selection({
  libelle,
  aide,
  erreur,
  options,
  ...reste
}: Props) {
  return (
    <Champ libelle={libelle} aide={aide} erreur={erreur}>
      {(liens) => (
        <select
          {...liens}
          {...reste}
          className={[
            'min-h-cible w-full rounded-2 border bg-fond-surface px-2',
            'text-14 text-texte',
            'focus-visible:outline focus-visible:outline-2',
            'focus-visible:outline-offset-2 focus-visible:outline-focus',
            erreur === undefined ? 'border-bord-champ' : 'border-erreur-bord',
          ].join(' ')}
        >
          {options.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </select>
      )}
    </Champ>
  );
}
